import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Resolve the session once on first load of an authenticated route.
 *
 * `fetchUser` resolves to the user, resolves to `null` when signed out (401),
 * and rejects when the session could not be checked (a 500, a timeout). A
 * rejection sets `sessionLoadFailed` so the app can offer a retry instead of
 * showing the login form to someone who is still signed in. `retrySession`
 * runs the bootstrap again.
 *
 * @param {{
 *   fetchUser: () => Promise<Record<string, any> | null>,
 *   isAuthFreeRoute: boolean,
 *   loggedIn: boolean,
 *   onUser?: (user: Record<string, any> | null) => void,
 * }} options
 * @returns {{ sessionResolved: boolean, sessionLoadFailed: boolean, retrySession: () => void }}
 */
export default function useSessionBootstrap({ fetchUser, isAuthFreeRoute, loggedIn, onUser }) {
  const sessionBootstrapStartedRef = useRef(loggedIn)
  const onUserRef = useRef(onUser)
  const [sessionResolved, setSessionResolved] = useState(loggedIn)
  const [sessionLoadFailed, setSessionLoadFailed] = useState(false)
  onUserRef.current = onUser

  const runSessionBootstrap = useCallback(() => {
    sessionBootstrapStartedRef.current = true
    fetchUser()
      .then(
        (user) => onUserRef.current?.(user),
        // establishCurrentUser already reported the failure.
        () => setSessionLoadFailed(true),
      )
      .finally(() => {
        setSessionResolved(true)
      })
  }, [fetchUser])

  useEffect(() => {
    if (isAuthFreeRoute || sessionBootstrapStartedRef.current) return
    runSessionBootstrap()
  }, [isAuthFreeRoute, runSessionBootstrap])

  const retrySession = useCallback(() => {
    setSessionLoadFailed(false)
    setSessionResolved(false)
    runSessionBootstrap()
  }, [runSessionBootstrap])

  return { sessionResolved, sessionLoadFailed, retrySession }
}
